import React from 'react';
import { Typography, Table, Collapse, Badge, Tag } from 'antd';
import { CheckCircleOutlined, WarningOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/**
 * Step component for reviewing the summary of uploaded products
 * 
 * @param {Object} props - Component properties
 * @param {Object} props.summary - Summary data of the upload
 * @param {Array} props.uploadedData - Data of the uploaded products
 * @param {boolean} props.parsingFile - Whether the file is being parsed
 */
const ReviewSummaryStep = ({ summary, uploadedData, parsingFile }) => {
    // 🛠️ Funzione helper per normalizzare i valori result (rimuove spazi e case sensitivity)
    const normalizeResult = (result) => {
        return result ? result.toString().trim() : '';
    };

    // Raggruppa i dati per tipo di risultato con ordine di priorità
    // 🛠️ Gestisce sia i valori UI (Available, Orderable, In Progress, Error) 
    // che i valori raw dell'API (found, orderable, in_production, invalid)
    const groupedData = {
        'Available': uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Available' || normalized === 'found';
        }),
        'Orderable': uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Orderable' || normalized === 'orderable';
        }),
        'In The Making': uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'In progress';
        }),
        'Error': uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return normalized === 'Error' || normalized === 'invalid';
        }),
        'Other': uploadedData.filter(item => {
            const normalized = normalizeResult(item.result);
            return !['Available', 'Orderable', 'In progress', 'Error', 'found', 'orderable', 'invalid'].includes(normalized);
        })
    };

    // Rimuovi gruppi vuoti
    Object.keys(groupedData).forEach(key => {
        if (groupedData[key].length === 0) {
            delete groupedData[key];
        }
    });



    // Configurazione delle colonne per le tabelle
    const getColumns = (resultType) => [
        {
            title: 'SKU Code',
            dataIndex: 'skuCode',
            key: 'skuCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'EAN Code',
            dataIndex: 'eanCode',
            key: 'eanCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'UPC Code',
            dataIndex: 'upcCode',
            key: 'upcCode',
            width: '25%',
            render: (text) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'result',
            key: 'result',
            width: '25%',
            render: (result) => {
                const getTagColor = (result) => {
                    const normalized = normalizeResult(result);
                    switch (normalized) {
                        case 'Available':
                        case 'found':
                            return 'success';
                        case 'Orderable':
                        case 'orderable':
                            return 'processing';
                        case 'In progress':
                        case 'in_production':
                            return 'warning';
                        case 'Error':
                        case 'invalid':
                            return 'error';
                        default: return 'default';
                    }
                };

                // 🛠️ Mappatura per il testo di visualizzazione
                const getDisplayText = (result) => {
                    const normalized = normalizeResult(result);
                    switch (normalized) {
                        case 'found': return 'Available';
                        case 'orderable': return 'Orderable';
                        case 'in_production': return 'In The Making';
                        case 'invalid': return 'Error';
                        case 'In progress': return 'In The Making';
                        default: return result;
                    }
                };

                return <Tag color={getTagColor(result)}>{getDisplayText(result)}</Tag>;
            },
        },
    ];

    // Configurazione degli accordion panels
    const getPanelConfig = (resultType) => {
        const configs = {
            'Available': {
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                color: '#52c41a',
                description: 'These items are available and will be selected by default (highest priority)'
            },
            'Orderable': {
                icon: <InfoCircleOutlined style={{ color: '#1890ff' }} />,
                color: '#1890ff',
                description: 'These items can be ordered and added to the catalogue (lower priority)'
            },
            'In The Making': {
                icon: <WarningOutlined style={{ color: '#faad14' }} />,
                color: '#faad14',
                description: 'These items are currently being manufactured and will be available soon'
            },
            'Error': {
                icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
                color: '#ff4d4f',
                description: 'These items have errors and cannot be processed'
            },
            'Other': {
                icon: <WarningOutlined style={{ color: '#faad14' }} />,
                color: '#faad14',
                description: 'These items have other status types'
            }
        };
        return configs[resultType] || configs['Other'];
    };

    return (
        <div style={{ padding: '20px 0' }}>
            <div style={{ marginBottom: '20px' }}>
                <Title level={5}>Analysis Results Summary</Title>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div>
                        <Text>Total items: </Text>
                        <Text strong>{summary.total || 0}</Text>
                    </div>
                    {summary.available > 0 && (
                        <div>
                            <Text>Available: </Text>
                            <Text strong style={{ color: '#52c41a' }}>{summary.available}</Text>
                        </div>
                    )}
                    {summary.orderable > 0 && (
                        <div>
                            <Text>Orderable: </Text>
                            <Text strong style={{ color: '#1890ff' }}>{summary.orderable}</Text>
                        </div>
                    )}
                    {summary.inProgress > 0 && (
                        <div>
                            <Text>In The Making: </Text>
                            <Text strong style={{ color: '#faad14' }}>{summary.inProgress}</Text>
                        </div>
                    )}
                    {summary.error > 0 && (
                        <div>
                            <Text>Errors: </Text>
                            <Text strong style={{ color: '#ff4d4f' }}>{summary.error}</Text>
                        </div>
                    )}
                    {summary.other > 0 && (
                        <div>
                            <Text>Other: </Text>
                            <Text strong style={{ color: '#faad14' }}>{summary.other}</Text>
                        </div>
                    )}
                </div>
            </div>

            {parsingFile ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Text>Loading results...</Text>
                </div>
            ) : (
                <Collapse
                    defaultActiveKey={[]}
                    style={{ backgroundColor: '#fafafa' }}
                    items={Object.entries(groupedData).map(([resultType, items]) => {
                        const config = getPanelConfig(resultType);
                        return {
                            key: resultType,
                            label: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {config.icon}
                                    <span style={{ fontWeight: 'bold' }}>{resultType}</span>
                                    <Badge
                                        count={items.length}
                                        style={{ backgroundColor: config.color }}
                                    />
                                </div>
                            ),
                            extra: (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {config.description}
                                    </Text>
                                </div>
                            ),
                            children: (
                                <Table
                                    columns={getColumns(resultType)}
                                    dataSource={items}
                                    rowKey={(record) => `${resultType}-${record.skuCode || record.eanCode || record.upcCode || Math.random()}`}
                                    pagination={false}
                                    size="small"
                                    scroll={{ y: 300 }}
                                    style={{ backgroundColor: '#fff' }}
                                />
                            )
                        };
                    })}
                />
            )}

            {(summary.available > 0 || summary.orderable > 0 || summary.inProgress > 0) && (
                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
                    <Text style={{ color: '#52c41a' }}>
                        <CheckCircleOutlined style={{ marginRight: '8px' }} />
                        {(() => {
                            const parts = [];
                            if (summary.available > 0) {
                                parts.push(`${summary.available} available item${summary.available > 1 ? 's' : ''} (selected by default)`);
                            }
                            if (summary.orderable > 0) {
                                parts.push(`${summary.orderable} orderable item${summary.orderable > 1 ? 's' : ''}`);
                            }
                            if (summary.inProgress > 0) {
                                parts.push(`${summary.inProgress} item${summary.inProgress > 1 ? 's' : ''} in the making`);
                            }

                            let message = parts.join(', ');
                            if (parts.length > 1) {
                                const lastComma = message.lastIndexOf(', ');
                                message = message.substring(0, lastComma) + ' and ' + message.substring(lastComma + 2);
                            }

                            return `${message} will be saved to the catalogue order when you proceed to the next step.`;
                        })()}
                    </Text>
                </div>
            )}

            {summary.error > 0 && (
                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px' }}>
                    <Text style={{ color: '#ff4d4f' }}>
                        <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
                        {summary.error} item{summary.error > 1 ? 's' : ''} with errors cannot be processed. Please check the data and try again if needed.
                    </Text>
                </div>
            )}
        </div>
    );
};

export default ReviewSummaryStep; 